import React, { useContext, useState } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";

export default function SellerProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [product] = useState(location.state?.item || null);

    if (!product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <h5>No product details available.</h5>
                    <Button variant="primary" className="mt-3" onClick={() => navigate(-1)}>
                        Back to list
                    </Button>
                </div>
            </PageLayout>
        );
    }

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "";

    // Helper to format key labels nicely (e.g. product_code -> Product Code)
    const formatLabel = (key) => {
        return key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Filter dynamic scalar fields that are non-null and not handled separately
    const detailEntries = Object.entries(product).filter(([key, value]) => {
        const ignoredKeys = [
            "id",
            "title",
            "card_img",
            "images",
            "category",
            "brand",
            "store",
            "description"
        ];
        return value !== null && value !== "" && !ignoredKeys.includes(key);
    });

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">Seller Product Details</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item"><Link to="#">Home</Link></li>
                                    <li className="mc-breadcrumb-item">Product Details</li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
                        </div>
                    </div>
                </Col>

                {/* Left Side: Images & Card Summary */}
                <Col lg={4}>
                    <Card className="mb-4">
                        <Card.Body className="text-center">
                            {product.card_img && (
                                <img
                                    src={`${imgBaseUrl}${product.card_img}`}
                                    alt={product.title || "Product Image"}
                                    className="img-fluid rounded mb-3"
                                    style={{ maxHeight: "250px", objectFit: "cover" }}
                                />
                            )}
                            {product.title && <h5>{product.title}</h5>}
                            {product.product_code && (
                                <Badge bg="primary">{product.product_code}</Badge>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Additional Images (Only shown if array has non-null images) */}
                    {Array.isArray(product.images) && product.images.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0">Product Gallery</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    {product.images.map(
                                        (imgObj) =>
                                            imgObj?.image && (
                                                <Col key={imgObj.id || imgObj.image} xs={4}>
                                                    <img
                                                        src={`${imgBaseUrl}${imgObj.image}`}
                                                        alt="Gallery"
                                                        className="img-fluid rounded border"
                                                        style={{ height: "70px", width: "100%", objectFit: "cover" }}
                                                    />
                                                </Col>
                                            )
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {/* Right Side: Product Details */}
                <Col lg={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Product Information</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                {/* Category */}
                                {product.category?.name && (
                                    <Col sm={6}>
                                        <strong>Category: </strong>
                                        <span>{product.category.name}</span>
                                    </Col>
                                )}

                                {/* Brand */}
                                {product.brand?.name && (
                                    <Col sm={6}>
                                        <strong>Brand: </strong>
                                        <span>{product.brand.name}</span>
                                    </Col>
                                )}

                                {/* Store */}
                                {product.store?.name && (
                                    <Col sm={6}>
                                        <strong>Store: </strong>
                                        <span>{product.store.name}</span>
                                    </Col>
                                )}

                                {/* Dynamic Non-Null Fields */}
                                {detailEntries.map(([key, value]) => (
                                    <Col sm={key.includes("description") ? 12 : 6} key={key}>
                                        <strong>{formatLabel(key)}: </strong>
                                        {key.includes("price") || key.includes("dollar") ? (
                                            <span>${value}</span>
                                        ) : (
                                            <span className="text-capitalize">{String(value)}</span>
                                        )}
                                    </Col>
                                ))}

                                {/* Description (HTML Content) */}
                                {product.description && (
                                    <Col sm={12}>
                                        <strong>Description: </strong>
                                        <div
                                            className="text-muted mb-0 mt-1"
                                            dangerouslySetInnerHTML={{ __html: product.description }}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}